import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  Divider,
  Slider,
  Switch,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  CircularProgress,
  LinearProgress,
  Chip,
  Avatar,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
// Fix the import for icons
import HomeIcon from '@mui/icons-material/Home';
import ShieldIcon from '@mui/icons-material/Shield';
import SendIcon from '@mui/icons-material/Send';
// Import ExpandMore directly instead of using the shorthand import
import ExpandMore from '@mui/icons-material/ExpandMore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`frontend-test-tabpanel-${index}`}
      aria-labelledby={`frontend-test-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const FrontendTest: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [sliderValue, setSliderValue] = useState<number>(50);
  const [switchValue, setSwitchValue] = useState<boolean>(false);
  const [checkboxValue, setCheckboxValue] = useState<boolean>(false);
  const [radioValue, setRadioValue] = useState<string>('option1');
  const [selectValue, setSelectValue] = useState<string>('');
  const [textFieldValue, setTextFieldValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleLoadingDemo = () => {
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
    }, 3000);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom>
        Frontend Testing Page v0.2
      </Typography>
      <Typography variant="subtitle1" gutterBottom sx={{ mb: 4 }}>
        This page is for testing various frontend components and features
      </Typography>

      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<HomeIcon />} label="Basic Components" />
          <Tab icon={<ShieldIcon />} label="Form Controls" />
          <Tab label="Layout & Feedback" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" gutterBottom>Basic UI Components</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Button Variants</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
                    <Button variant="contained" color="primary">Primary</Button>
                    <Button variant="contained" color="secondary">Secondary</Button>
                    <Button variant="outlined" color="primary">Outlined</Button>
                    <Button variant="text" color="primary">Text</Button>
                    <Button variant="contained" color="primary" disabled>Disabled</Button>
                    <Button variant="contained" color="success">Success</Button>
                    <Button variant="contained" color="error">Error</Button>
                    <Button variant="contained" color="warning">Warning</Button>
                    <Button variant="contained" color="info">Info</Button>
                    <Button variant="contained" startIcon={<SendIcon />}>With Icon</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Typography</Typography>
                  <Typography variant="h1">h1. Heading</Typography>
                  <Typography variant="h2">h2. Heading</Typography>
                  <Typography variant="h3">h3. Heading</Typography>
                  <Typography variant="h4">h4. Heading</Typography>
                  <Typography variant="h5">h5. Heading</Typography>
                  <Typography variant="h6">h6. Heading</Typography>
                  <Typography variant="subtitle1">subtitle1. Lorem ipsum dolor sit amet</Typography>
                  <Typography variant="subtitle2">subtitle2. Lorem ipsum dolor sit amet</Typography>
                  <Typography variant="body1">body1. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur</Typography>
                  <Typography variant="body2">body2. Lorem ipsum dolor sit amet, consectetur adipisicing elit. Quos blanditiis tenetur</Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Cards & Paper</Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={6}>
                      <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography>Elevation 1</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={3} sx={{ p: 2 }}>
                        <Typography>Elevation 3</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={6} sx={{ p: 2 }}>
                        <Typography>Elevation 6</Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper elevation={12} sx={{ p: 2 }}>
                        <Typography>Elevation 12</Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6">Chips & Badges</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                    <Chip label="Basic" />
                    <Chip label="Clickable" onClick={() => {}} />
                    <Chip label="Deletable" onDelete={() => {}} />
                    <Chip avatar={<Avatar>M</Avatar>} label="Avatar" />
                    <Chip label="Primary" color="primary" />
                    <Chip label="Success" color="success" />
                    <Chip label="Warning" color="warning" />
                    <Chip label="Error" color="error" />
                    <Chip label="Info" color="info" />
                    <Chip label="Secondary" color="secondary" />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>Form Controls</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Text Inputs</Typography>
                  
                  <TextField
                    fullWidth
                    label="Standard"
                    value={textFieldValue}
                    onChange={(e) => setTextFieldValue(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="With Helper Text"
                    helperText="Some important helper text"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type="password"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Error"
                    error
                    helperText="There is an error"
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Disabled"
                    disabled
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Multiline"
                    multiline
                    rows={4}
                    margin="normal"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Selectors</Typography>
                  
                  <FormControl fullWidth margin="normal">
                    <InputLabel id="select-label">Select</InputLabel>
                    <Select
                      labelId="select-label"
                      value={selectValue}
                      label="Select"
                      onChange={(e) => setSelectValue(e.target.value as string)}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      <MenuItem value="option1">Option 1</MenuItem>
                      <MenuItem value="option2">Option 2</MenuItem>
                      <MenuItem value="option3">Option 3</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl component="fieldset" margin="normal">
                    <FormLabel component="legend">Radio Group</FormLabel>
                    <RadioGroup 
                      value={radioValue}
                      onChange={(e) => setRadioValue(e.target.value)}
                    >
                      <FormControlLabel value="option1" control={<Radio />} label="Option 1" />
                      <FormControlLabel value="option2" control={<Radio />} label="Option 2" />
                      <FormControlLabel value="option3" control={<Radio />} label="Option 3" />
                    </RadioGroup>
                  </FormControl>

                  <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checkboxValue}
                          onChange={(e) => setCheckboxValue(e.target.checked)}
                        />
                      }
                      label="Checkbox"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={switchValue}
                          onChange={(e) => setSwitchValue(e.target.checked)}
                        />
                      }
                      label="Switch"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Sliders</Typography>
                  
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography gutterBottom>Basic Slider</Typography>
                    <Slider
                      value={sliderValue}
                      onChange={(e, newValue) => setSliderValue(newValue as number)}
                      aria-labelledby="continuous-slider"
                    />
                  </Box>
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography gutterBottom>Discrete Slider</Typography>
                    <Slider
                      value={sliderValue}
                      aria-labelledby="discrete-slider"
                      step={10}
                      marks
                      min={0}
                      max={100}
                      onChange={(e, newValue) => setSliderValue(newValue as number)}
                    />
                  </Box>
                  <Box sx={{ width: '100%', mt: 2 }}>
                    <Typography gutterBottom>Disabled Slider</Typography>
                    <Slider disabled defaultValue={30} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" gutterBottom>Layout & Feedback Components</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Progress Indicators</Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {isLoading ? 'Loading...' : 'Done'}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3 }}>
                    <CircularProgress />
                    <Button
                      variant="contained"
                      sx={{ mt: 2 }}
                      onClick={handleLoadingDemo}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Loading...' : 'Test Loading'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Alerts</Typography>
                  
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <AlertTitle>Error</AlertTitle>
                    This is an error alert — <strong>check it out!</strong>
                  </Alert>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    <AlertTitle>Warning</AlertTitle>
                    This is a warning alert — <strong>check it out!</strong>
                  </Alert>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>Info</AlertTitle>
                    This is an info alert — <strong>check it out!</strong>
                  </Alert>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <AlertTitle>Success</AlertTitle>
                    This is a success alert — <strong>check it out!</strong>
                  </Alert>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Accordions</Typography>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>Accordion 1</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
                        malesuada lacus ex, sit amet blandit leo lobortis eget.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>Accordion 2</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography>
                        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Suspendisse
                        malesuada lacus ex, sit amet blandit leo lobortis eget.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                  
                  <Accordion disabled>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography>Disabled Accordion</Typography>
                    </AccordionSummary>
                  </Accordion>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
        <Button 
          variant="contained" 
          color="primary" 
          href="/"
          sx={{ mr: 2 }}
        >
          Back to Home
        </Button>
        <Button 
          variant="outlined"
          onClick={() => window.location.reload()}
        >
          Reload Page
        </Button>
      </Box>
    </Box>
  );
}

export default FrontendTest;